import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";

const TIPOS_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/GetTiposLicenca/";
const SAVE_COLAB_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/SaveColabLicenca/";

const SectionTitle = styled.p`
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const Hint = styled.p`
  margin: 0 0 10px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #94a3b8;
  padding: 0 12px;
  box-sizing: border-box;
  outline: none;
  font-size: 13px;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`;

const ListShell = styled.div`
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }
`;

const ListHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  color: #475569;
  z-index: 1;
`;

const LicencaRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const LicencaLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const Counter = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: #263b4b;
`;

const formatLicencaLabel = (item) =>
  `${String(item?.fornecedor ?? "").trim()} - ${String(item?.tipo ?? "").trim()}`;

const normalizeObservacoes = (value) => {
  const str = value == null ? "" : String(value).trim();
  return str;
};

export default function ModalAdicionarLicencasColaborador({
  tipo = "COLAB",
  matricula,
  email,
  nomeExibicao = null,
  observacoes,
  idsTiposAtivos = [],
  onClose,
  onSaved,
}) {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const tipoVinculo =
    String(tipo ?? "COLAB").toUpperCase() === "TERCEIRO" ? "TERCEIRO" : "COLAB";
  const isTerceiro = tipoVinculo === "TERCEIRO";

  const [busca, setBusca] = useState("");
  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selecionados, setSelecionados] = useState(() => new Set());

  const idsAtivosSet = useMemo(() => {
    const set = new Set();
    (idsTiposAtivos ?? []).forEach((id) => {
      const key = String(id);
      if (key) set.add(key);
    });
    return set;
  }, [idsTiposAtivos]);

  const emailValido = useMemo(() => {
    const e = String(email || "").trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }, [email]);

  const nomeTerceiroValido = useMemo(() => {
    if (!isTerceiro) return true;
    return String(nomeExibicao || "").trim().length > 0;
  }, [isTerceiro, nomeExibicao]);

  useEffect(() => {
    const carregarTipos = async () => {
      setLoadingTipos(true);
      try {
        const { data } = await apiRef.current.get(TIPOS_LICENCA_ENDPOINT);
        const list = Array.isArray(data) ? data : data?.data ?? [];
        setTiposLicenca(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        alert(err?.response?.data?.message || "Erro ao carregar tipos de licença.");
      } finally {
        setLoadingTipos(false);
      }
    };

    carregarTipos();
  }, []);

  const disponiveis = useMemo(() => {
    return (tiposLicenca ?? []).filter((item) => !idsAtivosSet.has(String(item.id)));
  }, [idsAtivosSet, tiposLicenca]);

  const filtrados = useMemo(() => {
    const query = busca.trim().toUpperCase();
    if (!query) return disponiveis;
    return disponiveis.filter((item) =>
      formatLicencaLabel(item).toUpperCase().includes(query)
    );
  }, [busca, disponiveis]);

  const todosFiltradosSelecionados =
    filtrados.length > 0 && filtrados.every((item) => selecionados.has(String(item.id)));

  const toggleLicenca = (id) => {
    const key = String(id);
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTodos = () => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (todosFiltradosSelecionados) {
        filtrados.forEach((item) => next.delete(String(item.id)));
      } else {
        filtrados.forEach((item) => next.add(String(item.id)));
      }
      return next;
    });
  };

  const canSubmit =
    selecionados.size > 0 &&
    emailValido &&
    nomeTerceiroValido &&
    !saving &&
    !loadingTipos &&
    disponiveis.length > 0 &&
    (isTerceiro || String(matricula || "").trim().length === 6);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);

      const payload = {
        tipo: tipoVinculo,
        email: String(email || "").trim().toLowerCase(),
        observacoes: normalizeObservacoes(observacoes) || null,
        ids_tipo_licenca: Array.from(selecionados).map((id) => Number(id)),
      };

      if (isTerceiro) {
        payload.nome_exibicao = String(nomeExibicao || "").trim();
      } else {
        payload.matricula = String(matricula || "").trim();
      }

      await apiRef.current.post(SAVE_COLAB_LICENCA_ENDPOINT, payload);
      onClose?.();
      onSaved?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao adicionar licença(s).");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="md">
        <Formulario onSubmit={handleSubmit}>
          <SectionTitle>ADICIONAR LICENÇAS</SectionTitle>
          <Hint>
            Serão utilizados o e-mail
            {isTerceiro ? ", o nome" : ""} e as observações já informados no cadastro.
            {!emailValido && " Informe um e-mail válido antes de adicionar licenças."}
            {isTerceiro && !nomeTerceiroValido && " Informe o nome antes de adicionar licenças."}
          </Hint>

          <div>
            <SearchInput
              placeholder="Pesquisar por fornecedor ou tipo..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              disabled={loadingTipos || disponiveis.length === 0}
            />

            <ListShell>
              <ListHeader>
                <input
                  type="checkbox"
                  checked={todosFiltradosSelecionados}
                  onChange={toggleTodos}
                  disabled={filtrados.length === 0 || loadingTipos}
                  aria-label="Selecionar todos"
                />
                <span>Selecionar todos</span>
                <Counter>
                  {selecionados.size} selecionada
                  {selecionados.size === 1 ? "" : "s"} • {disponiveis.length} disponíve
                  {disponiveis.length === 1 ? "l" : "is"}
                </Counter>
              </ListHeader>

              {loadingTipos ? (
                <EmptyRow>Carregando tipos de licença...</EmptyRow>
              ) : disponiveis.length === 0 ? (
                <EmptyRow>Este cadastro já possui todas as licenças cadastradas.</EmptyRow>
              ) : filtrados.length === 0 ? (
                <EmptyRow>Nenhum tipo encontrado para a busca.</EmptyRow>
              ) : (
                filtrados.map((item) => {
                  const label = formatLicencaLabel(item);
                  return (
                    <LicencaRow key={item.id} title={label}>
                      <input
                        type="checkbox"
                        checked={selecionados.has(String(item.id))}
                        onChange={() => toggleLicenca(item.id)}
                      />
                      <LicencaLabel>{label}</LicencaLabel>
                    </LicencaRow>
                  );
                })
              )}
            </ListShell>
          </div>

          <FormComp
            type="submit"
            valor={saving ? "SALVANDO..." : "ADICIONAR SELECIONADAS"}
            disabled={!canSubmit}
          />
        </Formulario>
      </Modal>
    </Overlay>
  );
}
